import useCallbackRef from 'hooks/useCallbackRef';
import { SideDrawerRef } from '../components/SideDrawer/SideDrawer.component';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { DataRoomShareableLink } from 'models/ShareableLink';
import { DataRoomSelectableNode } from '../components/FileCheckSelector/types';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DataRoomContext from '../providers/DataRoom/DataRoom.context';
import { DataRoomNode } from '../models/DataRoomNode';
import { getIsArrayEmpty, sortDataRoomNodes } from 'utils';
import { prepareNodesForShareableLinkModalFileSelector } from '../providers/EditShareableLink/utils';
import dataRoomApi from '../dataRoomApi';
import { collectCheckedNodesForShareableLink } from '../components/FileCheckSelector/utils';

export default function useShareableLink() {
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);
  const [modal, modalRef] = useCallbackRef<ModalRef>(null);

  const [selectedLink, setSelectedLink] = useState<DataRoomShareableLink>();
  const [selectorNodes, setSelectorNodes] = useState<DataRoomSelectableNode[]>(
    [],
  );
  const [checkedFileIds, setCheckedFileIds] = useState<string[]>([]);
  const [checkedFolderIds, setCheckedFolderIds] = useState<string[]>([]);
  const [initiallyCheckedNodes, setInitiallyCheckedNodes] = useState<string[]>(
    [],
  );

  const [isLoading, setIsLoading] = useState(false);

  // selection the selector was seeded with, kept frozen while the user picks:
  // `collectCheckedNodesForShareableLink` needs it to resolve mixed folders
  // whose children have never been fetched
  const baselineFileIds = useRef<string[]>([]);

  const { popServerError } = useContext(PopNotificationsContext);
  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const onModalFilesSelect = useCallback(
    (nodes: DataRoomSelectableNode[]) => {
      modal?.close();

      const { fileIds, folderIds } = collectCheckedNodesForShareableLink(
        nodes,
        baselineFileIds.current,
      );

      setCheckedFileIds(fileIds);
      setCheckedFolderIds(folderIds);
    },
    [modal],
  );

  const prepareInitialSelectorNodes = useCallback(
    (
      selectedFileIds: string[],
      selectedFolderIds: string[],
      nodes: DataRoomNode[],
    ) => {
      const sortedNodes = sortDataRoomNodes(nodes);
      const pNodes = prepareNodesForShareableLinkModalFileSelector(
        sortedNodes,
        selectedFileIds,
      );

      baselineFileIds.current = selectedFileIds;

      setInitiallyCheckedNodes([...selectedFileIds, ...selectedFolderIds]);
      setCheckedFileIds(selectedFileIds);
      setSelectorNodes(pNodes);
    },
    [],
  );

  const loadSelectorNodes = useCallback(
    async (selectedFileIds: string[] = [], selectedFolderIds: string[] = []) => {
      setSelectorNodes([]);

      try {
        setIsLoading(true);

        const { data } = await dataRoomApi.nodes.getChildren(dataRoomId);

        prepareInitialSelectorNodes(selectedFileIds, selectedFolderIds, data);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [dataRoomId, prepareInitialSelectorNodes, popServerError],
  );

  const openModal = useCallback(
    async (selectedFileIds: string[] = [], selectedFolderIds: string[] = []) => {
      modal?.open();

      await loadSelectorNodes(selectedFileIds, selectedFolderIds);
    },
    [modal, loadSelectorNodes],
  );

  const openDrawer = useCallback(
    async (selectedFileIds: string[] = [], selectedFolderIds: string[] = []) => {
      drawer?.setOpen(true);

      await loadSelectorNodes(selectedFileIds, selectedFolderIds);
    },
    [drawer, loadSelectorNodes],
  );

  const onCloseDrawer = useCallback(() => setSelectorNodes([]), []);

  useEffect(() => {
    if (!selectedLink) return;

    const { fileIds, folderIds } = selectedLink;

    setCheckedFileIds(fileIds);
    setCheckedFolderIds(folderIds);
  }, [selectedLink]);

  // while the selector lives inside the drawer there is no confirm step, so
  // every change to the tree is collected the way the modal did on `Confirm`
  useEffect(() => {
    if (!drawer?.open || getIsArrayEmpty(selectorNodes)) return;

    const { fileIds, folderIds } = collectCheckedNodesForShareableLink(
      selectorNodes,
      baselineFileIds.current,
    );

    setCheckedFileIds(fileIds);
    setCheckedFolderIds(folderIds);
  }, [drawer, selectorNodes]);

  return {
    drawer,
    selectedLink,
    selectorNodes,
    isLoading,
    checkedFileIds,
    checkedFolderIds,
    initiallyCheckedNodes,
    drawerRef,
    modalRef,
    setSelectedLink,
    setSelectorNodes,
    setCheckedFileIds,
    setCheckedFolderIds,
    setIsLoading,
    openModal,
    openDrawer,
    onCloseDrawer,
    onModalFilesSelect,
  };
}
